import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DocumentPdfButton from "@/components/base/DocumentPdfButton";
import PrintButton from "@/components/base/PrintButton";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import ImmeubleForm from "../components/ImmeubleForm";
import { useImmeubles } from "../ImmeublesProvider";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { useLoyers } from "@/pages/proprietaire/loyers/LoyersProvider";
import { useDepenses } from "@/pages/proprietaire/depenses/DepensesProvider";
import { useMaintenance } from "@/pages/proprietaire/maintenance/MaintenanceProvider";
import { useDocuments } from "@/pages/proprietaire/documents/DocumentsProvider";
import { formatDate } from "@/utils/format";
import { exportSummaryPdf, printSummaryPdf, type PdfDocumentOptions, type PdfSection } from "@/utils/pdfDocument";

interface AccessModule {
  label: string;
  to: string;
  icon: string;
  hint: string;
}

const ACCESS_MODULES: AccessModule[] = [
  { label: "Biens", to: "/proprietaire/biens", icon: "ri-home-4-line", hint: "Appartements, studios, boutiques..." },
  { label: "Locataires", to: "/proprietaire/locataires", icon: "ri-user-3-line", hint: "Fiches des occupants" },
  { label: "Contrats", to: "/proprietaire/contrats", icon: "ri-file-text-line", hint: "Baux et échéances" },
  { label: "Loyers", to: "/proprietaire/loyers", icon: "ri-wallet-3-line", hint: "Suivi par période" },
  { label: "Impayés", to: "/proprietaire/impayes", icon: "ri-alert-line", hint: "Loyers non réglés" },
  { label: "Maintenance", to: "/proprietaire/maintenance", icon: "ri-tools-line", hint: "Demandes d'entretien" },
  { label: "Dépenses", to: "/proprietaire/depenses", icon: "ri-money-cny-circle-line", hint: "Charges de l'immeuble" },
  { label: "Documents", to: "/proprietaire/documents", icon: "ri-folder-line", hint: "Pièces et justificatifs" },
];

export default function ImmeubleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getImmeuble, removeImmeuble } = useImmeubles();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [toDelete, setToDelete] = useState(false);
  const { countByImmeuble } = useBiens();
  const { countByImmeuble: countContrats, countLocatairesByImmeuble } = useContrats();
  const { loyersByImmeuble, impayes } = useLoyers();
  const { countByImmeuble: countDepenses } = useDepenses();
  const { countByImmeuble: countInterventions } = useMaintenance();
  const { countByImmeuble: countDocuments } = useDocuments();
  const immeuble = id ? getImmeuble(id) : undefined;

  if (!immeuble) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/proprietaire/immeubles"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux immeubles
        </Link>
        <EmptyState
          icon="ri-building-2-line"
          title="Immeuble introuvable"
          description="Cet immeuble n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to="/proprietaire/immeubles"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les immeubles
            </Link>
          }
        />
      </div>
    );
  }

  const localisation = [immeuble.commune, immeuble.province, immeuble.region].filter(Boolean).join(" · ");
  const niveauxLabel = `${immeuble.niveaux} niveau${immeuble.niveaux > 1 ? "x" : ""}`;

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Région", value: immeuble.region || "—", icon: "ri-map-2-line" },
    { label: "Province", value: immeuble.province || "—", icon: "ri-community-line" },
    { label: "Commune / Ville", value: immeuble.commune || "—", icon: "ri-building-line" },
    { label: "Secteur / Quartier", value: immeuble.secteur || "—", icon: "ri-map-pin-2-line" },
    { label: "Adresse", value: immeuble.adresse || "—", icon: "ri-road-map-line" },
    { label: "Nombre de niveaux", value: niveauxLabel, icon: "ri-stack-line" },
  ];

  const buildPdfOptions = (): PdfDocumentOptions => {
    const sections: PdfSection[] = [
      {
        heading: "Informations de l'immeuble",
        fields: [
          { label: "Nom", value: immeuble.nom },
          ...infos.map((info) => ({ label: info.label, value: info.value })),
          { label: "Date d'ajout", value: formatDate(immeuble.createdAt) },
        ],
      },
    ];
    if (immeuble.description) {
      sections.push({ heading: "Description", note: immeuble.description });
    }
    if (immeuble.infosComplementaires) {
      sections.push({ heading: "Informations complémentaires", note: immeuble.infosComplementaires });
    }
    sections.push({
      heading: "Synthèse",
      fields: [
        { label: "Biens", value: String(countByImmeuble(immeuble.id)) },
        { label: "Locataires", value: String(countLocatairesByImmeuble(immeuble.id)) },
        { label: "Contrats", value: String(countContrats(immeuble.id)) },
        { label: "Loyers suivis", value: String(loyersByImmeuble(immeuble.id).length) },
        {
          label: "Impayés",
          value: String(impayes.filter((loyer) => loyer.immeubleId === immeuble.id).length),
        },
        { label: "Interventions de maintenance", value: String(countInterventions(immeuble.id)) },
        { label: "Dépenses", value: String(countDepenses(immeuble.id)) },
        { label: "Documents", value: String(countDocuments(immeuble.id)) },
      ],
    });
    return {
      filename: `immeuble-${immeuble.nom}`,
      title: immeuble.nom,
      subtitle: localisation || "Immeuble",
      sections,
    };
  };

  const handleExportPdf = () => exportSummaryPdf(buildPdfOptions());
  const handlePrint = () => printSummaryPdf(buildPdfOptions());

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/proprietaire/immeubles"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux immeubles
      </Link>

      <section className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-48 w-full overflow-hidden bg-background-100 md:h-64">
          {immeuble.photo ? (
            <img
              src={immeuble.photo}
              alt={immeuble.nom}
              title={`${immeuble.nom} — immeuble à ${immeuble.commune}`}
              className="h-48 w-full object-cover object-top md:h-64"
            />
          ) : (
            <div className="flex h-48 w-full items-center justify-center bg-background-100 text-foreground-300 md:h-64">
              <i className="ri-building-2-line text-6xl" aria-hidden="true" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 p-4 md:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="secondary" icon="ri-map-pin-2-line">
                {immeuble.commune}
              </Badge>
              <Badge tone="accent" icon="ri-stack-line">
                {niveauxLabel}
              </Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {immeuble.nom}
            </h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-map-pin-line text-base text-foreground-400" aria-hidden="true" />
              {localisation || "Localisation non renseignée"}
            </p>
            <p className="mt-1 text-xs text-foreground-400">Ajouté le {formatDate(immeuble.createdAt)}</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
            <Link
              to={`/proprietaire/biens?immeuble=${immeuble.id}&nouveau=1`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un bien
            </Link>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-edit-line text-base" aria-hidden="true" />
              Modifier
            </button>
            <button
              type="button"
              onClick={() => setToDelete(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
            >
              <i className="ri-delete-bin-line text-base" aria-hidden="true" />
              Supprimer
            </button>
            <DocumentPdfButton onExport={handleExportPdf} />
            <PrintButton onPrint={handlePrint} />
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations de l'immeuble
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            {immeuble.description && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Description
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{immeuble.description}</p>
              </div>
            )}

            {immeuble.infosComplementaires && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Informations complémentaires
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{immeuble.infosComplementaires}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {ACCESS_MODULES.map((module) => {
              const scoped: Record<string, string> = {
                Biens: `/proprietaire/biens?immeuble=${immeuble.id}`,
                Contrats: `/proprietaire/contrats?immeuble=${immeuble.id}`,
                Loyers: `/proprietaire/loyers?immeuble=${immeuble.id}`,
                Impayés: `/proprietaire/impayes?immeuble=${immeuble.id}`,
                Maintenance: `/proprietaire/maintenance?immeuble=${immeuble.id}`,
                Dépenses: `/proprietaire/depenses?immeuble=${immeuble.id}`,
                Documents: `/proprietaire/documents?immeuble=${immeuble.id}`,
              };
              const target = scoped[module.label] ?? module.to;
              const counts: Record<string, number> = {
                Biens: countByImmeuble(immeuble.id),
                Locataires: countLocatairesByImmeuble(immeuble.id),
                Contrats: countContrats(immeuble.id),
                Loyers: loyersByImmeuble(immeuble.id).length,
                Impayés: impayes.filter((loyer) => loyer.immeubleId === immeuble.id).length,
                Maintenance: countInterventions(immeuble.id),
                Dépenses: countDepenses(immeuble.id),
                Documents: countDocuments(immeuble.id),
              };
              const count = counts[module.label] ?? 0;
              return (
                <Link
                  key={module.to}
                  to={target}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={`${module.icon} text-base`} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-label font-medium text-foreground-950">
                      {module.label}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                  </span>
                  <span className="shrink-0 rounded-full bg-background-100 px-2 py-0.5 text-xs font-label font-medium text-foreground-600">
                    {count}
                  </span>
                  <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        </section>
      </div>

      <ImmeubleForm open={editOpen} initial={immeuble} onClose={() => setEditOpen(false)} />

      <ConfirmDeleteModal
        open={toDelete}
        onClose={() => setToDelete(false)}
        onConfirm={() => {
          removeImmeuble(immeuble.id);
          navigate("/proprietaire/immeubles");
        }}
        title="Supprimer l'immeuble"
        description={`Voulez-vous vraiment supprimer « ${immeuble.nom} » ? Cette action est définitive.`}
        blockedMessage={
          countByImmeuble(immeuble.id) > 0
            ? `Cet immeuble contient ${countByImmeuble(immeuble.id)} bien(s) rattaché(s). Supprimez ou déplacez d'abord ses biens.`
            : undefined
        }
      />
    </div>
  );
}